/**
 * Handlers - Sets up event handlers for voice interface
 * 
 * Required from parent (voiceInterface):
 * - shadowRoot: ShadowRoot | Document
 * - isMobile: boolean
 * - startVoiceCall(): Promise<void>
 * - endVoiceCall(): void
 * - toggleMute(): void
 * - toggleSpeaker(): void
 * - sendDesktopTextMessage(): void
 * - toggleConversationPanel(): void
 * - textHandlers: TextHandlers instance
 * 
 * State owned by this module:
 * - None (only sets up event listeners)
 */

export class Handlers {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    this.shadowRoot = voiceInterface.shadowRoot;
  }

  /**
   * Setup event handlers for voice interface
   */
  setupEventHandlers(callbacks) {
    const startCallBtn = this.shadowRoot.getElementById('startCallBtn');
    const endCallBtn = this.shadowRoot.getElementById('desktopEndCallBtn');
    const muteBtn = this.shadowRoot.getElementById('desktopMuteBtn');
    const speakerBtn = this.shadowRoot.getElementById('desktopSpeakerBtn');
    
    // Compact layout buttons (when history is expanded)
    const endCallBtnCompact = this.shadowRoot.getElementById('desktopEndCallBtnCompact');
    const muteBtnCompact = this.shadowRoot.getElementById('desktopMuteBtnCompact');
    const speakerBtnCompact = this.shadowRoot.getElementById('desktopSpeakerBtnCompact');
    
    if (startCallBtn) {
      startCallBtn.onclick = () => this.voiceInterface.startVoiceCall();
    }
    if (endCallBtn) {
      endCallBtn.onclick = () => this.voiceInterface.endVoiceCall();
    }
    if (muteBtn) {
      muteBtn.onclick = () => this.voiceInterface.toggleMute();
    }
    if (speakerBtn) {
      speakerBtn.onclick = () => this.voiceInterface.toggleSpeaker();
    }

    const pauseBtn = this.shadowRoot.getElementById('desktopPauseBtn');
    if (pauseBtn) {
      pauseBtn.onclick = () => this.voiceInterface.togglePause();
    }

    // Compact layout button handlers (same functionality)
    if (endCallBtnCompact) {
      endCallBtnCompact.onclick = () => this.voiceInterface.endVoiceCall();
    }
    if (muteBtnCompact) {
      muteBtnCompact.onclick = () => this.voiceInterface.toggleMute();
    }
    if (speakerBtnCompact) {
      speakerBtnCompact.onclick = () => this.voiceInterface.toggleSpeaker();
    }

    const pauseBtnCompact = this.shadowRoot.getElementById('desktopPauseBtnCompact');
    if (pauseBtnCompact) {
      pauseBtnCompact.onclick = () => this.voiceInterface.togglePause();
    }
    
    // Desktop text input handlers (only on desktop)
    if (!this.voiceInterface.isMobile) {
      const voiceTextInput = this.shadowRoot.getElementById('voiceTextInput');
      const voiceSendBtn = this.shadowRoot.getElementById('voiceSendBtn');
      
      if (voiceTextInput) {
        // Enable/disable send button based on input content
        voiceTextInput.addEventListener('input', () => {
          if (voiceSendBtn) {
            voiceSendBtn.disabled = !voiceTextInput.value.trim();
          }
        });
        
        // Handle Enter key
        voiceTextInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter' && !e.shiftKey && voiceTextInput.value.trim()) {
            e.preventDefault();
            if (voiceSendBtn && !voiceSendBtn.disabled) {
              voiceSendBtn.click();
            }
          }
        });
      }
      
      if (voiceSendBtn) {
        voiceSendBtn.onclick = () => {
          this.voiceInterface.sendDesktopTextMessage();
        };
      }
      
      // Conversation panel toggle
      const conversationHeader = this.shadowRoot.getElementById('conversationHeader');
      if (conversationHeader) {
        conversationHeader.addEventListener('click', () => {
          this.voiceInterface.transcript.toggleConversationPanel();
        });
      }
    }
  }

  /**
   * Setup mobile-specific event handlers
   * Uses document-level touch capture for Shopify/e-commerce themes where
   * touches may never reach our bar (overlays, theme capture, etc).
   */
  setupMobileEventHandlers() {
    if (!this.voiceInterface.isMobile) return;

    // Mobile bar controls (container-scoped for Shopify/theme ID conflicts)
    const container = document.getElementById('mobile-voice-call-bar-container');
    if (!container) return;
    const mobileMuteBtn = container.querySelector('#mobileMuteBtn');
    const mobilePauseBtn = container.querySelector('#mobilePauseBtn');
    const mobileSpeakerBtn = container.querySelector('#mobileSpeakerBtn');
    const mobileEndCallBtn = container.querySelector('#mobileEndCallBtn');
    const mobileTranscriptRow = container.querySelector('#mobileTranscriptRow');
    const mobileCloseTranscriptBtn = container.querySelector('#mobileCloseTranscriptBtn');
    const mobileTextInput = container.querySelector('#mobileTextInput');
    const mobileSendBtn = container.querySelector('#mobileSendBtn');

    const hitTest = (x, y, el) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };

    // Remove any previous document-level listeners (bar can be recreated between calls)
    this._mobileTouchCleanup?.();

    // Document-level touch capture: intercept touches before Shopify/theme handlers.
    // Only process when our bar exists and touch is within our controls area.
    let touchOrigin = null;
    const onTouchStart = (e) => {
      if (!document.getElementById('mobile-voice-call-bar-container') || !e.touches.length) return;
      touchOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onTouchEnd = (e) => {
      const container = document.getElementById('mobile-voice-call-bar-container');
      if (!container || !touchOrigin || !e.changedTouches.length) {
        touchOrigin = null;
        return;
      }
      const t = e.changedTouches[0];
      const x = t.clientX, y = t.clientY;
      const dx = Math.abs(x - touchOrigin.x);
      const dy = Math.abs(y - touchOrigin.y);
      touchOrigin = null;
      if (dx > 15 || dy > 15) return;
      const muteBtn = container.querySelector('#mobileMuteBtn');
      const pauseBtn = container.querySelector('#mobilePauseBtn');
      const speakerBtn = container.querySelector('#mobileSpeakerBtn');
      const endBtn = container.querySelector('#mobileEndCallBtn');
      const transcriptRow = container.querySelector('#mobileTranscriptRow');
      const closeTranscriptBtn = container.querySelector('#mobileCloseTranscriptBtn');
      const mobileSendBtnEl = container.querySelector('#mobileSendBtn');
      const mobileTextInputEl = container.querySelector('#mobileTextInput');
      const overlay = container.querySelector('#mobileTranscriptOverlay');
      const isOverlayVisible = !!overlay?.classList?.contains('mobile-transcript-overlay--open');
      if (hitTest(x, y, endBtn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.endVoiceCall();
      } else if (hitTest(x, y, muteBtn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.toggleMute();
      } else if (hitTest(x, y, pauseBtn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.togglePause();
      } else if (hitTest(x, y, speakerBtn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.toggleSpeaker();
      } else if (isOverlayVisible && hitTest(x, y, closeTranscriptBtn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.collapseMobileTranscript();
      } else if (isOverlayVisible && hitTest(x, y, mobileSendBtnEl) && mobileTextInputEl?.value?.trim()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.sendMobileTextMessage();
      } else if (
        this.voiceInterface.isActive &&
        !isOverlayVisible &&
        hitTest(x, y, transcriptRow)
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.voiceInterface.expandMobileTranscript();
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
    this._mobileTouchCleanup = () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
    };

    // Click handlers (fallback + non-touch)
    if (mobileMuteBtn) {
      mobileMuteBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.toggleMute(); });
    }
    if (mobilePauseBtn) {
      mobilePauseBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.togglePause(); });
    }
    if (mobileSpeakerBtn) {
      mobileSpeakerBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.toggleSpeaker(); });
    }
    if (mobileEndCallBtn) {
      mobileEndCallBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.endVoiceCall(); });
    }

    // Expand transcript overlay
    if (mobileTranscriptRow) {
      mobileTranscriptRow.addEventListener('click', (e) => {
        if (!this.voiceInterface.isActive) return;
        e.stopPropagation();
        e.preventDefault();
        this.voiceInterface.expandMobileTranscript();
      });
    }

    // Close transcript overlay
    if (mobileCloseTranscriptBtn) {
      mobileCloseTranscriptBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.collapseMobileTranscript(); });
    }

    // Mobile text input handlers (input + keyup for mobile Safari reliability)
    const syncSendButton = () => {
      if (mobileSendBtn && mobileTextInput) {
        mobileSendBtn.disabled = !mobileTextInput.value.trim();
      }
    };
    if (mobileTextInput) {
      mobileTextInput.addEventListener('input', syncSendButton);
      mobileTextInput.addEventListener('keyup', syncSendButton);

      mobileTextInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey && mobileTextInput.value.trim()) {
          e.preventDefault();
          if (mobileSendBtn && !mobileSendBtn.disabled) {
            mobileSendBtn.click();
          }
        }
      });
    }

    if (mobileSendBtn) {
      mobileSendBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.voiceInterface.sendMobileTextMessage(); });
    }
  }
}
